// oxlint-disable no-console
import { PositionBracket, PositionBracketEntry } from '../../apis/v2/snapshot/types';
import Num, { BigNumber } from '../num';

const allowLogging = false;

/**
 * ERD: https://www.notion.so/prextech/Position-Bracket-2bceb32ad22780098176f869b4d1cb7b
 * Calculation: https://www.notion.so/prextech/Position-bracket-order-form-forumla-2c4eb32ad22780f19630ddca44a1be92
 *
 *
 *
 * - L(max) = max leverage of the position tier that P(total) corresponds to
 * - L(pos_max) = max leverage of the position tier that P(new) corresponds to
 * - P(pos_max) = max position size at the tier that L(new) corresponds to
 *
 */

export const applyBracketMultiplier = (positionBracket: PositionBracket, multiplier: string = '1'): PositionBracket => {
  const mult = Num(multiplier);
  if (!mult.isFinite() || mult.isLessThanOrEqualTo(0)) return positionBracket;
  if (mult.isEqualTo(1)) return positionBracket;

  const entries = positionBracket.entries.map((entry, index) => ({
    ...entry,
    notionalPositionSizeUpperLimit: Num(entry.notionalPositionSizeUpperLimit).multipliedBy(mult).toString(),
    notionalPositionSizeLowerLimit:
      index === 0
        ? '0'
        : Num(positionBracket.entries[index - 1].notionalPositionSizeUpperLimit)
            .multipliedBy(mult)
            .toString(),
  }));

  return {
    ...positionBracket,
    entries,
  };
};

const findBracketTierByPositionSize = (positionSize: BigNumber, positionBracket: PositionBracket) => {
  for (const entry of positionBracket.entries) {
    const min = Num(entry.notionalPositionSizeLowerLimit);
    const max = Num(entry.notionalPositionSizeUpperLimit);
    if (positionSize.isGreaterThan(min) && positionSize.isLessThanOrEqualTo(max)) return entry;
  }
  return positionBracket.entries[0];
};

export const findBracketTierByLeverage = (leverage: number, positionBracket: PositionBracket) => {
  for (const entry of positionBracket.entries) {
    const min = entry.minInitLeverage;
    const max = entry.maxInitLeverage;
    if (leverage > min && leverage <= max) return entry;
  }
  return positionBracket.entries[0];
};

const multiPositionBracketFormula = {
  // L(total) = (P + P(new)) / (M + P(new)/L(new)) ≤ L(max)
  // isValid = L(total) ≤ L(max), L(max) = max leverage of the position tier that P(total) corresponds to
  symbolLevel: (
    existingPositionSize: BigNumber, // P
    newPositionSize: BigNumber, // P(new)
    existingMargin: BigNumber, // M
    newLeverage: number, // L(new)
    positionBracket: PositionBracket,
    options?: {
      minLeverage?: number;
      availableMargin?: BigNumber;
    },
  ) => {
    const availableMargin = options?.availableMargin || Num(0);
    const existingEffectiveLeverage = !existingMargin.isZero()
      ? existingPositionSize.dividedBy(existingMargin)
      : Num(1);
    const newMargin = newLeverage !== 0 ? newPositionSize.dividedBy(newLeverage) : Num(0);
    const totalPositionSize = existingPositionSize.plus(newPositionSize); // P(total)
    const totalMargin = existingMargin.plus(newMargin); // M(total)
    const totalLeverage = !totalMargin.isZero()
      ? totalPositionSize.dividedBy(totalMargin).decimalPlaces(6, BigNumber.ROUND_HALF_UP)
      : Num(0); // L(total)

    const targetTier = findBracketTierByPositionSize(totalPositionSize, positionBracket);
    const targetMaxLeverage = options?.minLeverage
      ? Math.max(targetTier.maxInitLeverage, options.minLeverage)
      : targetTier.maxInitLeverage; // L(max)

    const isValid = totalLeverage.isLessThanOrEqualTo(targetMaxLeverage); // L(total) ≤ L(max)

    const maxPositionSize = Num(
      findBracketTierByLeverage(totalLeverage.toNumber(), positionBracket).notionalPositionSizeUpperLimit,
    );

    const suggestMaxPositionSize = getMaxPositionSize(
      Num(newLeverage),
      availableMargin,
      existingPositionSize,
      existingEffectiveLeverage,
      positionBracket,
    );

    const suggestMaxMargin = getMaxMargin(
      Num(newLeverage),
      availableMargin,
      existingPositionSize,
      existingEffectiveLeverage,
      positionBracket,
    );

    const suggestMaxLeverage = getMaxLeverage(
      newPositionSize,
      targetTier.maxInitLeverage,
      existingPositionSize,
      existingEffectiveLeverage,
      positionBracket,
    );

    if (allowLogging) {
      const legacyAvailablePositionSize = maxPositionSize.minus(existingPositionSize);
      const legacyAvailableMargin = legacyAvailablePositionSize.dividedBy(newLeverage);

      console.group('[multi] [symbol]');
      console.log('[legacy]', {
        positionSize: legacyAvailablePositionSize.toString(),
        margin: legacyAvailableMargin.toString(),
        leverage: totalLeverage.toString(),
      });
      console.log('[new]', {
        positionSize: suggestMaxPositionSize?.toString(),
        margin: suggestMaxMargin?.toString(),
        leverage: suggestMaxLeverage?.toString(),
      });
      console.groupEnd();

      console.group(`[Bracket] [multi position] [symbol level] isValid: ${isValid}`);
      console.log(
        `P = ${existingPositionSize.toString()} M = ${existingMargin.toString()}  L = ${existingEffectiveLeverage}`,
      );
      console.log(`P(new) = ${newPositionSize.toString()} M(new) = ${newMargin.toString()}  L(new) = ${newLeverage}`);
      console.log(
        `P(total) = ${totalPositionSize.toString()} M(total) = ${totalMargin.toString()}  L(total) = ${totalLeverage.toNumber()}`,
      );
      console.log(
        `L(max) = ${targetMaxLeverage.toString()} L(total) = ${totalLeverage.toNumber()} ${isValid} ${maxPositionSize.toString()} `,
      );
      console.log(
        `L(max) = ${targetMaxLeverage.toString()} Avail. position ${legacyAvailablePositionSize.toString()} ${isValid} ${maxPositionSize.toString()} `,
      );
      console.groupEnd();
    }

    return {
      suggestMaxPositionSize: suggestMaxPositionSize || Num(0),
      suggestMaxMargin: suggestMaxMargin || Num(0),
      suggestMaxLeverage,
      isValid,
    };
  },
  positionLevel: {
    validateLeverage: (
      currentPositionSize: BigNumber,
      newPositionSize: BigNumber,
      currentMargin: BigNumber,
      newLeverage: number,
      positionBracket: PositionBracket,
      options?: {
        minLeverage?: number;
        availableMargin?: BigNumber;
      },
    ) => {
      const totalPositionSize = currentPositionSize.plus(newPositionSize);
      const targetTier = findBracketTierByPositionSize(totalPositionSize, positionBracket); // L(pos_max)
      const targetLeverage = options?.minLeverage
        ? Math.max(targetTier.maxInitLeverage, options.minLeverage)
        : targetTier.maxInitLeverage;

      const isValid = newLeverage <= targetLeverage;
      const currentEffectiveLeverage = !currentMargin.isZero() ? currentPositionSize.dividedBy(currentMargin) : Num(0);

      const suggestMaxLeverage = getMaxLeverage(
        currentPositionSize,
        targetTier.maxInitLeverage,
        Num(0), // 레버리지를 수정하는 것은 포지션 전체를 다시 new PositionSize를 추가하는 형태로 생각하기 때문에, exsitingPositionSize가 0이 된다.
        currentEffectiveLeverage,
        positionBracket,
      );

      if (allowLogging) {
        console.group('[multi] [position]');
        console.log({ legacyLeverage: targetLeverage, newLeverage: suggestMaxLeverage?.toString() });
        console.groupEnd();

        console.group(`[Bracket] [multi-position] [position level] [leverage] isValid: ${isValid}`);
        console.log(`P = ${currentPositionSize.toString()}`);
        console.log(`P(new) = ${newPositionSize.toString()}  L(new) = ${newLeverage}`);
        console.log(`P(total) = ${totalPositionSize.toString()}  L(pos_max) = ${targetLeverage}`);
        console.groupEnd();
      }

      return {
        isValid,
        suggestMaxLeverage,
      };
    },

    // L(new) = (P + P(new)) / (M + M(new)) ≤ L(pos_max)
    // isValid = L(new) ≤ L(pos_max), L(pos_max) = max leverage of the position tier that P(new) corresponds to
    validatePositionSize: (
      currentPositionSize: BigNumber, // P
      newPositionSize: BigNumber, // P(new)
      currentMargin: BigNumber, // M
      newMargin: BigNumber, // M(new)
      positionBracket: PositionBracket,
      options?: {
        minLeverage?: number;
        availableMargin?: BigNumber;
      },
    ) => {
      const availableMargin = options?.availableMargin || Num(0);
      const totalPositionSize = currentPositionSize.plus(newPositionSize); // P(total)
      const totalMargin = currentMargin.plus(newMargin); // M(total)
      const totalLeverage = !totalMargin.isZero()
        ? totalPositionSize.dividedBy(totalMargin).decimalPlaces(6, BigNumber.ROUND_HALF_UP)
        : Num(0); // L(total)
      const newLeverage = !totalMargin.isZero()
        ? totalPositionSize.dividedBy(totalMargin).decimalPlaces(6, BigNumber.ROUND_HALF_UP)
        : Num(1); // L(new)

      const targetTier = findBracketTierByPositionSize(totalPositionSize, positionBracket);
      const targetMaxLeverage = options?.minLeverage
        ? Math.max(targetTier.maxInitLeverage, options.minLeverage)
        : targetTier.maxInitLeverage; // L(pos_max)

      const isValid = newLeverage.isLessThanOrEqualTo(targetMaxLeverage); // L(new) ≤ L(pos_max)

      const maxPositionSize = Num(
        findBracketTierByLeverage(newLeverage.toNumber(), positionBracket).notionalPositionSizeUpperLimit,
      ); // P(pos_max)

      const suggestMaxPositionSize = getMaxPositionSize(
        newLeverage,
        availableMargin,
        currentPositionSize,
        Num(0),
        positionBracket,
      );

      const suggestMaxMargin = getMaxMargin(newLeverage, availableMargin, currentPositionSize, Num(0), positionBracket);

      const currentTier = findBracketTierByPositionSize(totalPositionSize, positionBracket);
      const currentMaxPositionSize = Num(currentTier.notionalPositionSizeUpperLimit);

      if (allowLogging) {
        // P + P(new) ≤ P(pos_max)
        // P(new) ≤ P(pos_max) - P
        const legacyAvailablePositionSize = maxPositionSize.minus(currentPositionSize);
        const legacyAvailableMargin = legacyAvailablePositionSize.dividedBy(newLeverage);

        console.group('[multi] [position]');
        console.log('[legacy]', {
          positionSize: legacyAvailablePositionSize.toString(),
          margin: legacyAvailableMargin.toString(),
          leverage: totalLeverage.toString(),
          newLeverage: newLeverage.toString(),
          targetMaxLeverage: targetMaxLeverage.toString(),
          isValid,
        });
        console.log('[new]', {
          positionSize: suggestMaxPositionSize?.toString(),
          margin: suggestMaxMargin?.toString(),
        });
        console.groupEnd();

        console.group(
          `[Bracket] [multi-position] [position level] [leverage] isValid: ${newLeverage.isLessThanOrEqualTo(targetMaxLeverage)}`,
        );
        console.log(
          `P = ${currentPositionSize.toString()} M = ${currentMargin.toString()} L = ${currentMargin.isZero() ? 1 : currentPositionSize.dividedBy(currentMargin).toNumber()}`,
        );
        console.log(
          `P(new) = ${newPositionSize.toString()} M(new) = ${newMargin.toString()} L(new) = ${newLeverage.toNumber()}`,
        );
        console.log(
          `P(total) = ${totalPositionSize.toString()} M(total) = ${totalMargin.toString()} L(total) = ${totalLeverage.toNumber()}`,
        );
        console.log(
          `L(max) = ${targetMaxLeverage.toString()} Avail. position ${legacyAvailablePositionSize.toString()} ${isValid} ${currentMaxPositionSize.toString()} `,
        );
        console.groupEnd();
      }

      return {
        suggestMaxPositionSize: suggestMaxPositionSize || Num(0),
        suggestMaxMargin: suggestMaxMargin || Num(0),
        isValid,
      };
    },
  },
};

const oneWayBracketFormula = {
  validate: (
    existingPositionSize: BigNumber, // P
    newPositionSize: BigNumber, // P(new)
    existingMargin: BigNumber, // M
    newMargin: BigNumber, // M(new)
    positionBracket: PositionBracket,
    options?: {
      minLeverage?: number;
      availableMargin?: BigNumber;
    },
  ) => {
    const availableMargin = options?.availableMargin || Num(0);
    const totalPositionSize = existingPositionSize.plus(newPositionSize); // P(total)
    const totalMargin = existingMargin.plus(newMargin); // M(total)

    // symbol level check (isValid = L(total) ≤ L(max))
    // L(total) = (P + P(new)) / (M + M(new)) ≤ L(max)
    const totalLeverage = !totalMargin.isZero()
      ? totalPositionSize.dividedBy(totalMargin).decimalPlaces(6, BigNumber.ROUND_HALF_UP)
      : Num(0); // L(total)

    const symbolLevelTier = findBracketTierByPositionSize(totalPositionSize, positionBracket);
    const symbolLevelMaxLeverage = options?.minLeverage
      ? Math.max(symbolLevelTier.maxInitLeverage, options.minLeverage)
      : symbolLevelTier.maxInitLeverage; // L(max)
    const isValidSymbolLevel = totalLeverage.isLessThanOrEqualTo(symbolLevelMaxLeverage);

    // position level check (isValid = L(new) ≤ L(pos_max))
    // P + P(new) = L(new) * (M + M(new)) ≤ P(pos_max)
    // (P + P(new)) / (M + M(new)) = L(new)
    // @note actually L(new) === L(total) in one-way mode
    const newLeverage = totalLeverage;

    const positionLevelTier = findBracketTierByPositionSize(newPositionSize, positionBracket);
    const positionLevelMaxLeverage = options?.minLeverage
      ? Math.max(positionLevelTier.maxInitLeverage, options.minLeverage)
      : positionLevelTier.maxInitLeverage; // L(pos_max)
    const isValidPositionLevel = newLeverage.isLessThanOrEqualTo(positionLevelMaxLeverage);

    const isValid = isValidSymbolLevel && isValidPositionLevel;

    const maxPositionSize = Num(
      findBracketTierByLeverage(newLeverage.toNumber(), positionBracket).notionalPositionSizeUpperLimit,
    ); // P(pos_max)

    const availableLeverage = Math.min(symbolLevelMaxLeverage, positionLevelMaxLeverage);
    const existingEffectiveLeverage = !existingMargin.isZero()
      ? existingPositionSize.dividedBy(existingMargin)
      : Num(0);

    // one way mode에서는 position level 기준으로 suggest value를 보여준다.
    // oneway mode는 existing(position 전체) === current(현재 position)
    const suggestMaxPositionSize = getMaxPositionSize(
      newLeverage,
      availableMargin,
      existingPositionSize,
      existingEffectiveLeverage,
      positionBracket,
    );

    const suggestMaxMargin = getMaxMargin(
      Num(newLeverage),
      availableMargin,
      existingPositionSize,
      existingEffectiveLeverage,
      positionBracket,
    );

    const suggestMaxLeverage = getMaxLeverage(
      existingPositionSize,
      availableLeverage,
      Num(0), // 레버리지를 수정하는 것은 포지션 전체를 다시 new PositionSize를 추가하는 형태로 생각하기 때문에, exsitingPositionSize가 0이 된다.
      existingEffectiveLeverage,
      positionBracket,
    );

    if (allowLogging) {
      // P + P(new) ≤ P(pos_max)
      // P(new) ≤ P(pos_max) - P
      const legacyAvailablePositionSize = maxPositionSize.minus(existingPositionSize);
      const legacyAvailableMargin = legacyAvailablePositionSize.dividedBy(newLeverage);

      console.group('[oneway]');
      console.log('[legacy]', {
        positionSize: legacyAvailablePositionSize.toString(),
        margin: legacyAvailableMargin.toString(),
        leverage: availableLeverage.toString(),
      });
      console.log('[new]', {
        positionSize: suggestMaxPositionSize?.toString(),
        margin: suggestMaxMargin?.toString(),
        leverage: suggestMaxLeverage?.toString(),
      });
      console.groupEnd();

      console.group(`[Bracket] [one-way] [leverage] isValid: ${isValid}`);
      console.log(
        `P = ${existingPositionSize.toString()}\t\t M = ${existingMargin.toString()} \t\t L = ${existingPositionSize.dividedBy(existingMargin)}`,
      );
      console.log(
        `P(new) = ${newPositionSize.toString()}\t\t M(new) = ${newMargin.toString()} \t\t L(new) = ${newPositionSize.dividedBy(newMargin).toNumber()}`,
      );
      console.log(
        `P(total) = ${totalPositionSize.toString()}\t\t M(total) = ${totalMargin.toString()} \t\t L(total) = ${totalLeverage}`,
      );
      console.log(
        `L(Max) = ${symbolLevelMaxLeverage}\t\t L(pos_max) = ${positionLevelMaxLeverage.toString()} \t\t L(total) = ${totalLeverage}`,
      );
      console.groupEnd();
    }

    return {
      suggestMaxPositionSize: suggestMaxPositionSize || Num(0),
      suggestMaxMargin: suggestMaxMargin || Num(0),
      suggestMaxLeverage,
      isValid,
    };
  },
};

interface ValidateBracketPositionSizeParams {
  tradeMode: 'MultiplePositions' | 'OneWay';
  symbolLevelParams: {
    existingPositionSize: BigNumber; // P
    newPositionSize: BigNumber; // P(new)
    existingMargin: BigNumber; // M
    newLeverage: number; // L(new)
  };
  positionLevelParams: {
    currentPositionSize: BigNumber; // P
    newPositionSize: BigNumber; // P(new)
    currentMargin: BigNumber; // M
    newMargin: BigNumber; // M(new)
  };
  positionBracket: PositionBracket;
  positionBracketMultiplier?: string;
  options?: {
    minLeverage?: number;
    availableMargin?: BigNumber;
  };
}

export const validateBracketPositionSize = ({
  tradeMode,
  symbolLevelParams,
  positionLevelParams,
  positionBracket: rawPositionBracket,
  positionBracketMultiplier,
  options,
}: ValidateBracketPositionSizeParams) => {
  const positionBracket = applyBracketMultiplier(rawPositionBracket, positionBracketMultiplier);
  switch (tradeMode) {
    case 'MultiplePositions': {
      const symbolLevelResult = multiPositionBracketFormula.symbolLevel(
        symbolLevelParams.existingPositionSize,
        symbolLevelParams.newPositionSize,
        symbolLevelParams.existingMargin,
        symbolLevelParams.newLeverage,
        positionBracket,
        options,
      );

      const positionLevelResult = multiPositionBracketFormula.positionLevel.validatePositionSize(
        positionLevelParams.currentPositionSize,
        positionLevelParams.newPositionSize,
        positionLevelParams.currentMargin,
        positionLevelParams.newMargin,
        positionBracket,
        options,
      );

      const isValid = symbolLevelResult.isValid && positionLevelResult.isValid;

      const maxAvailableMargin = BigNumber.min(
        !symbolLevelResult.isValid && symbolLevelResult.suggestMaxMargin
          ? symbolLevelResult.suggestMaxMargin
          : Infinity,
        !positionLevelResult.isValid && positionLevelResult.suggestMaxMargin
          ? positionLevelResult.suggestMaxMargin
          : Infinity,
      );

      const maxAvailablePositionSize = BigNumber.min(
        !symbolLevelResult.isValid && symbolLevelResult.suggestMaxPositionSize
          ? symbolLevelResult.suggestMaxPositionSize
          : Infinity,
        !positionLevelResult.isValid && positionLevelResult.suggestMaxPositionSize
          ? positionLevelResult.suggestMaxPositionSize
          : Infinity,
      );

      return {
        isValid,
        maxAvailableMargin: maxAvailableMargin.multipliedBy(0.99),
        maxAvailablePositionSize: maxAvailablePositionSize.multipliedBy(0.99),
      };
    }
    case 'OneWay': {
      const bracketResult = oneWayBracketFormula.validate(
        symbolLevelParams.existingPositionSize,
        symbolLevelParams.newPositionSize,
        symbolLevelParams.existingMargin,
        // adjust margin 때, 유저가 입력한 new margin을 써야하는데,
        // symbol level에서는 new margin 값이 없기 때문에, one way에서는 편의상 position level의 new margin을 사용한다.
        positionLevelParams.newMargin,
        positionBracket,
        options,
      );

      return {
        isValid: bracketResult.isValid,
        maxAvailableMargin: bracketResult.suggestMaxMargin?.multipliedBy(0.99) || Num(0),
        maxAvailablePositionSize: bracketResult.suggestMaxPositionSize?.multipliedBy(0.99) || Num(0),
      };
    }
  }
};

interface ValidateBracketLeverageParams {
  tradeMode: 'MultiplePositions' | 'OneWay';
  symbolLevelParams: {
    existingPositionSize: BigNumber; // P
    newPositionSize: BigNumber; // P(new)
    existingMargin: BigNumber; // M
    newLeverage: number; // L(new)
  };
  positionLevelParams: {
    currentPositionSize: BigNumber; // P
    currentMargin: BigNumber;
    newPositionSize: BigNumber; // P(new)
    newLeverage: number; // L(new)
  };
  positionBracket: PositionBracket;
  positionBracketMultiplier?: string;
  options?: {
    minLeverage?: number;
    availableMargin?: BigNumber;
  };
}

export const validateBracketLeverage = ({
  tradeMode,
  symbolLevelParams,
  positionLevelParams,
  positionBracket: rawPositionBracket,
  positionBracketMultiplier,
  options,
}: ValidateBracketLeverageParams) => {
  const positionBracket = applyBracketMultiplier(rawPositionBracket, positionBracketMultiplier);
  switch (tradeMode) {
    case 'MultiplePositions': {
      const symbolLevelResult = multiPositionBracketFormula.symbolLevel(
        symbolLevelParams.existingPositionSize,
        symbolLevelParams.newPositionSize,
        symbolLevelParams.existingMargin,
        symbolLevelParams.newLeverage,
        positionBracket,
        options,
      );

      const positionLevelResult = multiPositionBracketFormula.positionLevel.validateLeverage(
        positionLevelParams.currentPositionSize,
        positionLevelParams.newPositionSize,
        positionLevelParams.currentMargin,
        positionLevelParams.newLeverage,
        positionBracket,
        options,
      );

      const isValid = symbolLevelResult.isValid && positionLevelResult.isValid;

      const maxAvailableLeverage = Math.floor(
        Math.min(
          symbolLevelResult.isValid ? Infinity : symbolLevelResult.suggestMaxLeverage || Infinity,
          positionLevelResult.isValid ? Infinity : positionLevelResult.suggestMaxLeverage || Infinity,
        ),
      );

      return {
        isValid,
        maxAvailableLeverage,
      };
    }
    case 'OneWay': {
      const newLeverage = symbolLevelParams.newLeverage;
      const newMargin = !Num(newLeverage).isZero() ? symbolLevelParams.newPositionSize.dividedBy(newLeverage) : Num(0);

      const bracketResult = oneWayBracketFormula.validate(
        symbolLevelParams.existingPositionSize,
        symbolLevelParams.newPositionSize,
        symbolLevelParams.existingMargin,
        newMargin,
        positionBracket,
        options,
      );

      const availableLeverage = bracketResult?.suggestMaxLeverage || 0;

      const isValid = Num(newLeverage).isLessThanOrEqualTo(availableLeverage);
      const maxAvailableLeverage = Math.floor(availableLeverage);
      return {
        isValid,
        maxAvailableLeverage,
      };
    }
  }
};

const marginThreshold = (
  newLeverage: BigNumber,
  currentNotionalPositionSize: BigNumber,
  currentMargin: BigNumber,
  entry: PositionBracketEntry,
): BigNumber => {
  const maxLeverage = Num(entry.maxInitLeverage);

  if (newLeverage.isEqualTo(maxLeverage)) {
    return Num(0);
  }

  // (L(max) * M - P) / (L(new) - L(max))
  return maxLeverage
    .multipliedBy(currentMargin)
    .minus(currentNotionalPositionSize)
    .dividedBy(newLeverage.minus(maxLeverage));
};

const getMaxMargin = (
  newLeverage: BigNumber,
  availableMargin: BigNumber,
  currentNotionalPositionSize: BigNumber,
  currentEffectiveLeverage: BigNumber,
  positionBracket: PositionBracket,
): BigNumber | null => {
  if (newLeverage.isLessThanOrEqualTo(0)) return null;

  const currentMargin = !currentEffectiveLeverage.isZero()
    ? currentNotionalPositionSize.abs().dividedBy(currentEffectiveLeverage)
    : Num(0);

  const candidates: BigNumber[] = [];

  for (const entry of positionBracket.entries) {
    const maxLeverage = Num(entry.maxInitLeverage);
    const threshold = marginThreshold(newLeverage, currentNotionalPositionSize, currentMargin, entry);

    const upperLimitOfTier = Num(entry.notionalPositionSizeUpperLimit).minus(currentNotionalPositionSize);
    const lowerLimitOfTier = Num(entry.notionalPositionSizeLowerLimit).minus(currentNotionalPositionSize);

    if (maxLeverage.isLessThan(newLeverage)) {
      const candidate = BigNumber.max(
        BigNumber.min(availableMargin, upperLimitOfTier.dividedBy(newLeverage), threshold),
        Num(0),
      );

      if (candidate.isGreaterThan(lowerLimitOfTier.dividedBy(newLeverage))) {
        candidates.push(candidate);
      }
    } else if (maxLeverage.isGreaterThan(newLeverage)) {
      const candidate = BigNumber.max(BigNumber.min(availableMargin, upperLimitOfTier.dividedBy(newLeverage)), Num(0));

      if (
        candidate.isGreaterThan(lowerLimitOfTier.dividedBy(newLeverage)) &&
        candidate.isGreaterThanOrEqualTo(threshold)
      ) {
        candidates.push(candidate);
      }
    } else {
      if (currentEffectiveLeverage.isLessThanOrEqualTo(maxLeverage)) {
        const candidate = BigNumber.max(
          BigNumber.min(availableMargin, upperLimitOfTier.dividedBy(newLeverage)),
          Num(0),
        );

        if (candidate.isGreaterThan(lowerLimitOfTier.dividedBy(newLeverage))) {
          candidates.push(candidate);
        }
      }
    }
  }

  if (candidates.length === 0) return null;
  return BigNumber.max(...candidates);
};

const positionSizeThreshold = (
  newLeverage: BigNumber,
  currentNotionalPositionSize: BigNumber,
  currentMargin: BigNumber,
  entry: PositionBracketEntry,
): BigNumber => {
  const maxLeverage = Num(entry.maxInitLeverage);

  if (newLeverage.isEqualTo(maxLeverage)) return Num(0);

  // (L(max) * M - P) / (1 - L(max) / L(new))
  return maxLeverage
    .multipliedBy(currentMargin)
    .minus(currentNotionalPositionSize)
    .dividedBy(Num(1).minus(maxLeverage.dividedBy(newLeverage)));
};

const getMaxPositionSize = (
  newLeverage: BigNumber,
  availableMargin: BigNumber,
  currentNotionalPositionSize: BigNumber,
  currentEffectiveLeverage: BigNumber,
  positionBracket: PositionBracket,
): BigNumber | null => {
  if (newLeverage.isZero()) return null;

  const currentMargin = !currentEffectiveLeverage.isZero()
    ? currentNotionalPositionSize.abs().dividedBy(currentEffectiveLeverage)
    : Num(0);

  const candidates: BigNumber[] = [];

  for (const entry of positionBracket.entries) {
    const maxLeverage = Num(entry.maxInitLeverage);
    const threshold = positionSizeThreshold(newLeverage, currentNotionalPositionSize, currentMargin, entry);

    const upperLimitOfTier = Num(entry.notionalPositionSizeUpperLimit).minus(currentNotionalPositionSize);
    const lowerLimitOfTier = Num(entry.notionalPositionSizeLowerLimit).minus(currentNotionalPositionSize);

    if (maxLeverage.isLessThan(newLeverage)) {
      const candidate = BigNumber.max(
        BigNumber.min(threshold, availableMargin.multipliedBy(newLeverage), upperLimitOfTier),
        Num(0),
      );

      if (candidate.isGreaterThan(lowerLimitOfTier)) {
        candidates.push(candidate);
      }
    } else if (maxLeverage.isGreaterThan(newLeverage)) {
      const candidate = BigNumber.max(
        BigNumber.min(availableMargin.multipliedBy(newLeverage), upperLimitOfTier),
        Num(0),
      );

      if (candidate.isGreaterThan(lowerLimitOfTier) && candidate.isGreaterThanOrEqualTo(threshold)) {
        candidates.push(candidate);
      }
    } else {
      if (currentEffectiveLeverage.isLessThanOrEqualTo(maxLeverage)) {
        const candidate = BigNumber.max(
          BigNumber.min(availableMargin.multipliedBy(newLeverage), upperLimitOfTier),
          Num(0),
        );

        if (candidate.isGreaterThan(lowerLimitOfTier)) {
          candidates.push(candidate);
        }
      }
    }
  }

  if (candidates.length === 0) return null;
  return BigNumber.max(...candidates);
};

const getMaxLeverage = (
  newPositionSize: BigNumber,
  maxTierLeverage: number,
  existingPositionSize: BigNumber,
  existingEffectiveLeverage: BigNumber,
  positionBracket: PositionBracket,
): number | null => {
  const currentMargin = !existingEffectiveLeverage.isZero()
    ? existingPositionSize.abs().dividedBy(existingEffectiveLeverage)
    : Num(0);

  const totalPositionSize = existingPositionSize.plus(newPositionSize);
  const candidates: number[] = [];

  for (const entry of positionBracket.entries) {
    const lowerLimit = Num(entry.notionalPositionSizeLowerLimit);
    const upperLimit = Num(entry.notionalPositionSizeUpperLimit);
    const isInRange = totalPositionSize.isGreaterThan(lowerLimit) && totalPositionSize.isLessThanOrEqualTo(upperLimit);
    if (!isInRange) continue;

    const maxLeverage = Num(entry.maxInitLeverage);

    // candidate = (maxLeverage * newPositionSize) / (totalPositionSize - maxLeverage * currentMargin)
    const numerator = maxLeverage.multipliedBy(newPositionSize);
    const denominator = totalPositionSize.minus(maxLeverage.multipliedBy(currentMargin));
    if (denominator.isZero()) continue;
    const candidate = numerator.dividedBy(denominator);

    const isValidCandidate = candidate.isGreaterThanOrEqualTo(1) && candidate.isLessThanOrEqualTo(maxTierLeverage);
    if (!isValidCandidate) continue;

    candidates.push(candidate.integerValue(BigNumber.ROUND_FLOOR).toNumber());
  }

  if (candidates.length === 0) return null;
  return Math.max(...candidates);
};
